import { ProductionUnit, DeprecProdUnit } from '@api/models'
import { useFormContext } from 'react-hook-form'
import { useFormFieldProps } from '@hooks'
import { Form, Grid, Input } from '@components/common'
import { useCallback } from 'react'
import ProductionUnitsPeriodSelector from './ProductionUnitsPeriodSelector'

interface ProductionUnitsFormProps {
  formId: string
  type: 'add' | 'edit'
  onSubmit: (data: ProductionUnit) => Promise<void>
  initialValues: Partial<ProductionUnit>
  assetCompanyID: number
  assetID: string
  disabled?: boolean
  readonly?: boolean
  existingProductionUnits?: DeprecProdUnit[]
  onFirstAvailablePeriodChange?: (firstPeriod: number | null) => void
  onFirstAvailableYearChange?: (firstYear: number | null) => void
}

const ProductionUnitsForm = ({
  formId,
  type,
  onSubmit,
  assetCompanyID,
  assetID,
  disabled = false,
  readonly = false,
  existingProductionUnits = [],
  onFirstAvailablePeriodChange,
  onFirstAvailableYearChange,
}: ProductionUnitsFormProps) => {
  const {
    register,
    formState: { errors, isSubmitting },
    handleSubmit,
    watch,
    reset,
    setValue,
  } = useFormContext<ProductionUnit>()

  const fieldLabels = {
    UnitsProduced: 'Units Produced',
  }

  const { getInputProps } = useFormFieldProps<ProductionUnit>({
    isSubmitting,
    errors: errors as any,
    fields: fieldLabels as any,
    register,
  })

  const fiscalYear = watch('FiscalYear')
  const fiscalPeriod = watch('FiscalPeriod')


  const handleYearChange = useCallback(
    (year: number) => {
      setValue('FiscalYear', year)
    },
    [setValue]
  )

  const handlePeriodChange = useCallback(
    (period: number, endDate: string) => {
      setValue('FiscalPeriod', period)
      setValue('PeriodEndDate', endDate)
    },
    [setValue]
  )

  return (
    <Form id={formId} onSubmit={handleSubmit(onSubmit)}>
      <Grid>
        <ProductionUnitsPeriodSelector
          type={type}
          assetCompanyID={assetCompanyID}
          assetID={assetID}
          fiscalYear={fiscalYear}
          fiscalPeriod={fiscalPeriod}
          onYearChange={handleYearChange}
          onPeriodChange={handlePeriodChange}
          disabled={disabled || type === 'edit'}
          readonly={readonly}
          existingProductionUnits={existingProductionUnits}
          onFirstAvailablePeriodChange={onFirstAvailablePeriodChange}
          onFirstAvailableYearChange={onFirstAvailableYearChange}
          errors={{
            FiscalYear: errors.FiscalYear,
            FiscalPeriod: errors.FiscalPeriod,
          }}
        />
        <Grid.Item>
          <Input
            {...getInputProps('UnitsProduced', 'number')}
            disabled={disabled || !fiscalYear || !fiscalPeriod}
            readOnly={readonly}
            autoFocus={type === 'edit'}
          />
        </Grid.Item>
      </Grid>
    </Form>
  )
}

export default ProductionUnitsForm
